import * as React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../Components/Tabs';
import { Button } from '../../../Components/Button';
import Loader from '../../../Components/Loader';
import FileUpload from '../../../Components/FileUpload';
import { apiGet } from '../../../DataService/apiClient';
import { AssetCardRender } from '../../../Components/AssetCard';
import { AdminIcon } from '../../../Icons/AdminIcon';
import { FreebieDto, FreebieService } from '../../../DataService/FreebieService';
import { toast } from 'react-toastify';
import './FreebeeForm.css';

type BunnyFileDto = {
  fileName: string;
  folder: 'Videos' | 'Documents';
  url: string;
  fileSizeBytes: number;
  lastModified: string;
  contentType?: string | null;
};

type FreebieFile = {
  fileUrl: string;
  fileName: string;
  fileMimeType?: string;
  fileSizeBytes: number;
};

type FreebieFormState = {
  title: string;
  description?: string;
  fileUrl: string;
  fileName?: string;
  fileMimeType?: string;
  fileSizeBytes?: number;
  isActive: boolean;
  requireEmail: boolean;
  allowMultipleDownloadsPerEmail: boolean;
  maxDownloads?: number;
  availableFrom?: string;
  availableUntil?: string;
};

const toDateInputValue = (value?: string) => value ? value.slice(0, 10) : undefined;

type Props = {
  mode: 'create' | 'edit';
  freebie?: FreebieDto | null;
  onCancel: () => void;
  onSaved: () => void;
};

const emptyForm: FreebieFormState = {
  title: '',
  description: '',
  fileUrl: '',
  isActive: true,
  requireEmail: true,
  allowMultipleDownloadsPerEmail: false,
};

const steps = ['step-1', 'step-2', 'step-3', 'step-4', 'step-5'] as const;
type Step = typeof steps[number];

export const FreebieWizard = ({ mode, freebie, onCancel, onSaved }: Props) => {
  const [step, setStep] = React.useState<Step>('step-1');
  const [form, setForm] = React.useState<FreebieFormState>(emptyForm);
  const [availableFiles, setAvailableFiles] = React.useState<BunnyFileDto[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = React.useState(false);
  const [freebieFile, setFreebieFile] = React.useState<FreebieFile | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (freebie) {
      const nextFile = {
        fileUrl: freebie.fileUrl,
        fileName: freebie.fileName,
        fileMimeType: freebie.fileMimeType,
        fileSizeBytes: freebie.fileSizeBytes ?? 0,
      };

      setForm({
        title: freebie.title,
        description: freebie.description,
        fileUrl: freebie.fileUrl,
        fileName: freebie.fileName,
        fileMimeType: freebie.fileMimeType,
        fileSizeBytes: freebie.fileSizeBytes,
        isActive: freebie.isActive,
        requireEmail: freebie.requireEmail,
        allowMultipleDownloadsPerEmail: freebie.allowMultipleDownloadsPerEmail,
        maxDownloads: freebie.maxDownloads,
        availableFrom: toDateInputValue(freebie.availableFrom),
        availableUntil: toDateInputValue(freebie.availableUntil),
      });
      setFreebieFile(nextFile);
    } else {
      setForm(emptyForm);
      setFreebieFile(null);
    }
    setStep('step-1');
  }, [freebie, mode]);

  React.useEffect(() => {
    let cancelled = false;

    const loadFiles = async () => {
      setIsLoadingFiles(true);
      try {
        const res = await apiGet<BunnyFileDto[]>('/api/admin/files');
        if (!cancelled) setAvailableFiles(res);
      } finally {
        if (!cancelled) setIsLoadingFiles(false);
      }
    };

    loadFiles();
    return () => { cancelled = true; };
  }, []);

  const mapBunnyFileToFreebieFile = (file: BunnyFileDto): FreebieFile => ({
    fileUrl: file.url,
    fileName: file.fileName,
    fileMimeType: file.contentType ?? undefined,
    fileSizeBytes: file.fileSizeBytes,
  });

  const submit = async () => {
    if (!freebieFile) return;

    setIsSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        fileUrl: freebieFile.fileUrl,
        fileName: freebieFile.fileName,
        fileMimeType: freebieFile.fileMimeType,
        fileSizeBytes: freebieFile.fileSizeBytes,
        isActive: form.isActive,
        requireEmail: form.requireEmail,
        allowMultipleDownloadsPerEmail: form.allowMultipleDownloadsPerEmail,
        maxDownloads: form.maxDownloads,
        availableFrom: form.availableFrom,
        availableUntil: form.availableUntil,
      };

      const result = mode === 'edit' && freebie
        ? await FreebieService.update(freebie.id, payload)
        : await FreebieService.create(payload);

      toast.success(result.message ?? (mode === 'edit' ? 'Freebie uppdaterad' : 'Freebie skapad'));
      setStep('step-5');
      onSaved();
    } catch (error) {
      toast.error('Något gick fel vid sparandet');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepReady =
    !isSubmitting;

  const isStepOneComplete = form.title.trim().length > 0 && (form.description ?? '').trim().length > 0;
  const isStepTwoComplete = freebieFile !== null;
  const isStepThreeComplete = true;
  const maxUnlockedStepIndex = isStepOneComplete
    ? (isStepTwoComplete
      ? (isStepThreeComplete ? 3 : 2)
      : 1)
    : 0;

  const canNavigateToStep = (targetStep: Step) => steps.indexOf(targetStep) <= maxUnlockedStepIndex;

  const handleStepClick = (targetStep: Step) => {
    if (!canNavigateToStep(targetStep)) return;
    setStep(targetStep);
  };

  const canAdvanceFromCurrentStep =
    step === 'step-1'
      ? isStepOneComplete
      : step === 'step-2'
        ? isStepTwoComplete
        : step === 'step-3'
          ? isStepThreeComplete
          : false;

  return (
    <div className="freebie-wizard">
      <div className="campaign-form-header">
        <div>
          <h3>{mode === 'edit' ? 'Redigera freebie' : 'Skapa freebie'}</h3>
          <p>Stegvis flow för titel, fil, inställningar och kontroll.</p>
        </div>
        <span className="admin-pill">{mode === 'edit' ? 'Edit' : 'Create'}</span>
      </div>

      <StepIndicator currentStep={step} onStepClick={handleStepClick} canNavigateToStep={canNavigateToStep} />

      <div className="freebie-step-panel">
        {step === 'step-1' && (
          <div className="campaign-space-y-4">
            <input className="admin-campain-input" placeholder="Titel" value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} />
            <textarea className="admin-campain-input admin-campain-input--textarea" placeholder="Beskrivning" value={form.description ?? ''} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
          </div>
        )}

        {step === 'step-2' && (
          <Tabs defaultValue="upload" className="tabContainer">
            <TabsList>
              <TabsTrigger value="upload">Ladda upp</TabsTrigger>
              <TabsTrigger value="library">Bibliotek</TabsTrigger>
            </TabsList>

            <TabsContent value="upload">
              <FileUpload
                onDocumentUploaded={(doc) => {
                  const mapped = { fileUrl: doc.bunnyUrl, fileName: doc.fileName, fileMimeType: doc.mimeType, fileSizeBytes: doc.fileSizeBytes };
                  setFreebieFile(mapped);
                  setForm(prev => ({ ...prev, ...mapped }));
                }}
                onVideoUploaded={(video) => {
                  const mapped = { fileUrl: video.url, fileName: video.fileName, fileMimeType: video.contentType ?? undefined, fileSizeBytes: video.fileSizeBytes };
                  setFreebieFile(mapped);
                  setForm(prev => ({ ...prev, ...mapped }));
                }}
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/*"
              />
            </TabsContent>

            <TabsContent value="library">
              {isLoadingFiles ? <Loader loaderSize="MEDIUM" /> : (
                <AssetCardRender
                  data={availableFiles}
                  item={form}
                  onClick={(file) => {
                    const mapped = mapBunnyFileToFreebieFile(file);
                    setFreebieFile(mapped);
                    setForm(prev => ({ ...prev, ...mapped }));
                  }}
                />
              )}
            </TabsContent>
          </Tabs>
        )}

        {step === 'step-3' && (
          <div className="freebie-config-grid">
            <label><input type="checkbox" checked={form.isActive} onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))} /> Aktiv</label>
            <label><input type="checkbox" checked={form.requireEmail} onChange={e => setForm(prev => ({ ...prev, requireEmail: e.target.checked }))} /> Kräver email</label>
            <label><input type="checkbox" checked={form.allowMultipleDownloadsPerEmail} onChange={e => setForm(prev => ({ ...prev, allowMultipleDownloadsPerEmail: e.target.checked }))} /> Tillåt flera nedladdningar per email</label>
            <label>Max downloads<input className="admin-campain-input" type="number" value={form.maxDownloads ?? ''} onChange={e => setForm(prev => ({ ...prev, maxDownloads: e.target.value === '' ? undefined : Number(e.target.value) }))} /></label>
            <label>Giltig från<input className="admin-campain-input" type="date" value={form.availableFrom ?? ''} onChange={e => setForm(prev => ({ ...prev, availableFrom: e.target.value }))} /></label>
            <label>Giltig till<input className="admin-campain-input" type="date" value={form.availableUntil ?? ''} onChange={e => setForm(prev => ({ ...prev, availableUntil: e.target.value }))} /></label>
          </div>
        )}

        {step === 'step-4' && (
          <div className="freebie-preview-card">
            <h4>Förhandsgranskning</h4>
            <p>{form.title}</p>
            <p>{form.description}</p>
            <p>{freebieFile?.fileName}</p>
            <p>
              Giltig från: {form.availableFrom ? new Date(form.availableFrom).toLocaleDateString('sv-SE') : 'Oändligt'}
            </p>
            <p>
              Giltig till: {form.availableUntil ? new Date(form.availableUntil).toLocaleDateString('sv-SE') : 'Oändligt'}
            </p>
          </div>
        )}

        {step === 'step-5' && (
          <div className="freebie-preview-card">
            <AdminIcon.CheckCircle fill="hsl(var(--secondary))" />
            <h4>Klart</h4>
            <p>Freebie är sparad.</p>
            <div className="campaign-footer-buttons" style={{ justifyContent: 'flex-start', marginTop: '1rem' }}>
              <Button className="submit-button" onClick={onCancel} text="Till listan" />
            </div>
          </div>
        )}
      </div>

      <div className="campaign-footer-buttons">
        <Button className="submit-button" onClick={onCancel} text="Tillbaka" />
        {step !== 'step-4' && step !== 'step-5' ? (
          <Button className="submit-button" onClick={() => setStep(steps[Math.min(steps.indexOf(step) + 1, steps.length - 1)])} disabled={!stepReady || !canAdvanceFromCurrentStep} text={isSubmitting ? 'Sparar...' : 'Nästa'} />
        ) : null}
        {step === 'step-4' && (
          <Button className="submit-button" onClick={submit} disabled={!stepReady || !isStepTwoComplete} text={isSubmitting ? 'Sparar...' : (mode === 'edit' ? 'Uppdatera' : 'Skapa freebie')} />
        )}
      </div>
    </div>
  );
};

type StepIndicatorProps = {
  currentStep: Step;
  onStepClick: (step: Step) => void;
  canNavigateToStep: (step: Step) => boolean;
};

const StepIndicator = ({ currentStep, onStepClick, canNavigateToStep }: StepIndicatorProps) => {
  const currentIndex = steps.indexOf(currentStep);
  const stepLabel = (step: Step) => {
    switch (step) {
      case 'step-1': return 'Info';
      case 'step-2': return 'Fil';
      case 'step-3': return 'Inställningar';
      case 'step-4': return 'Kontroll';
      case 'step-5': return 'Klart';
      default: return step;
    }
  };

  return (
    <div className="step-indicator">
      <div className="step-indicator__track-wrapper">
        <div className="step-indicator__track-bg" />
        <div className="step-indicator__track-progress" style={{ width: `${(currentIndex / (steps.length - 1)) * 100}%` }} />
        {steps.map((step, index) => {
          const isActive = step === currentStep;
          const isCompleted = index < currentIndex;
          const isClickable = canNavigateToStep(step);
          return (
            <button key={step} type="button" className={['step-indicator__step', isClickable && 'step-indicator__step--clickable', !isClickable && !isActive && 'step-indicator__step--disabled'].filter(Boolean).join(' ')} onClick={() => onStepClick(step)}>
              <div className={['step-indicator__circle', isActive && 'step-indicator__circle--active', isCompleted && 'step-indicator__circle--completed', !isActive && !isCompleted && 'step-indicator__circle--inactive'].filter(Boolean).join(' ')}>
                {isCompleted ? <svg className="step-indicator__check" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg> : <span className="step-indicator__number">{index + 1}</span>}
              </div>
              <div className={['step-indicator__label', isActive && 'step-indicator__label--active'].filter(Boolean).join(' ')}>{stepLabel(step)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
